/* eslint-disable @typescript-eslint/no-use-before-define */

import { z } from "zod"
import { useCallback, useState } from "preact/hooks"
import { FORM_ERROR } from "final-form"
import { Field, Form, FormSpy } from "react-final-form"
import css from "../style.module.scss"
import { useSupport } from "../hooks/support"
import { $user } from "../hooks/user"
import { replace } from "../hooks/navigation"
import { t } from "../hooks/translations"
import { OffersScreen } from "./OffersScreen"
import { SocialAuth } from "../components/SocialAuth"

const signUpSchema = z
  .object({
    email: z.string().email().trim(),
    isoCountry: z.string(),
    newPassword: z.string().min(6).trim(),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  })

type SignUpValues = z.infer<typeof signUpSchema>

export const SignUpScreen = function SignUpScreen() {
  return (
    <div className={css["flex-container"]}>
      <div className={css["offers-screen"]}>
        <OffersScreen showContinueButton={false} />
      </div>

      <div className={css.splitter} />

      <div className={css["signup-screen"]}>
        <SignUp />
      </div>
    </div>
  )
}

const SignUp = function SignUp() {
  const {
    iso_countries: isoCountries,
    default_iso_country: defaultIsoCountry,
    auth_providers: authProviders,
  } = useSupport()
  const hasSocialAuth = Boolean(authProviders?.apple?.enabled || authProviders?.google?.enabled)

  const [initialValues] = useState<SignUpValues>(() => ({
    email: $user.get().email,
    isoCountry:
      $user.get().isoCountry ?? isoCountries.find(({ iso }) => iso === defaultIsoCountry).iso,
  }))
  const [showEmailForm, setShowEmailForm] = useState(false)
  const shouldShowEmailForm = showEmailForm

  const onSubmit = useCallback(async (values: SignUpValues) => {
    const result = signUpSchema.safeParse(values)
    if (!result.success) {
      return result.error.flatten().fieldErrors
    }

    try {
      const response = await fetch("/users/create_with_confirmation", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          user: {
            email: result.data.email,
            isoCountry: result.data.isoCountry,
            password: result.data.newPassword,
            passwordConfirm: result.data.confirmPassword,
          },
        }),
      })

      const json = await response.json()
      if (json.success) {
        $user.setKey("signedIn", true)
        replace("Payment")
        return {}
      }

      if (json.errors != null) {
        return { ...json, [FORM_ERROR]: json.errors }
      }

      return json
    } catch (error) {
      return { [FORM_ERROR]: String(error) }
    }
  }, [])

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ submitting, pristine, submitError, handleSubmit }) => (
        <form className={css.signup} onSubmit={handleSubmit}>
          <FormSpy
            subscription={{ values: true }}
            onChange={(state) => {
              $user.setKey("email", state.values.email)
              $user.setKey("isoCountry", state.values.isoCountry)
            }}
          />

          <h3 className={css.title}>{t("your_account")}</h3>
          <p className={css.subtitle}>{t("create_an_account")}</p>
          <p className={css["signup-offer"]}>
            {t("already_a_member")}{" "}
            <a
              className={css["signup-offer-link"]}
              href="/sign_in"
              onClick={(ev) => {
                ev.preventDefault()
                replace("SignIn")
              }}
            >
              {t("log_in")}
            </a>
          </p>
          {!shouldShowEmailForm && (
            <>
              {hasSocialAuth && <SocialAuth authProviders={authProviders} mode="signUp" />}
              {hasSocialAuth && <p className={css.or}>{t("or")}</p>}
              <button className={css.button} type="button" onClick={() => setShowEmailForm(true)}>
                {t("sign_up_with_email")}
              </button>
            </>
          )}

          {shouldShowEmailForm && (
            <>
              <fieldset className={css["signup-fieldset"]}>
                <Field<string>
                  name="email"
                  render={({ input, meta }) => (
                    <label htmlFor="signup-email">
                      <input
                        {...input}
                        className={css["signup-input"]}
                        type="email"
                        autoComplete="email"
                        placeholder="Email address"
                        id="signup-email"
                        disabled={submitting}
                      />
                      {(meta.error || meta.submitError) && meta.touched && (
                        <div className={css["signup-field-error"]}>
                          {meta.error || meta.submitError}
                        </div>
                      )}
                    </label>
                  )}
                />
                <Field<string>
                  name="isoCountry"
                  render={({ input, meta }) => (
                    <label htmlFor="signup-iso-country">
                      <div className={css["select-wrapper"]}>
                        <select
                          {...input}
                          className={css["signup-select"]}
                          id="signup-iso-country"
                          autoComplete="country-name"
                        >
                          {isoCountries.map(({ iso, name }) => (
                            <option id={iso} value={iso}>
                              {name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {(meta.error || meta.submitError) && meta.touched && (
                        <div className={css["signup-field-error"]}>
                          {meta.error || meta.submitError}
                        </div>
                      )}
                    </label>
                  )}
                />
                <Field<string>
                  name="newPassword"
                  render={({ input, meta }) => (
                    <label htmlFor="signup-new-password">
                      <input
                        {...input}
                        className={css["signup-input"]}
                        type="password"
                        autoComplete="new-password"
                        placeholder="Password"
                        id="signup-new-password"
                        disabled={submitting}
                      />
                      {(meta.error || meta.submitError) && meta.touched && (
                        <div className={css["signup-field-error"]}>
                          {meta.error || meta.submitError}
                        </div>
                      )}
                    </label>
                  )}
                />
                <Field<string>
                  name="confirmPassword"
                  render={({ input, meta }) => (
                    <label htmlFor="signup-confirm-password">
                      <input
                        {...input}
                        className={css["signup-input"]}
                        type="password"
                        autoComplete="new-password"
                        placeholder="Confirm Password"
                        id="signup-confirm-password"
                        disabled={submitting}
                      />
                      {(meta.error || meta.submitError) && meta.touched && (
                        <div className={css["signup-field-error"]}>
                          {meta.error || meta.submitError}
                        </div>
                      )}
                    </label>
                  )}
                />
              </fieldset>

              {submitError && <div className={css["submit-error"]}>{submitError}</div>}

              <button
                className={css.button}
                style={{ marginBottom: 16 }}
                type="submit"
                disabled={submitting || pristine}
              >
                {t("continue")}
              </button>
            </>
          )}
        </form>
      )}
    />
  )
}
